import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Trash2, Calculator } from "lucide-react";
import { toast } from "sonner";
import { api, tl, errMsg, todayStr, shiftDate, fmtDate } from "../lib/api";
import { useBusiness } from "../context/BusinessContext";
import { StatCard } from "../components/StatCard";
import { DiffBadge } from "../components/DiffBadge";
import { CategoryIcon } from "../components/CategoryIcon";
import { PageHeader, card, btnPrimary, btnGhost } from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function EntryForm({ bid, date, categories, staff, onDone }) {
  const [type, setType] = useState("income");
  const [f, setF] = useState({ category_id: "", staff_id: "", pay_type: "avans", amount: "", note: "" });
  const cats = categories.filter((c) => c.type === type);
  const isStaff = type === "staff";

  const submit = async (e) => {
    e.preventDefault();
    if (isStaff ? !f.staff_id : !f.category_id) return toast.error(isStaff ? "Personel seçin" : "Kategori seçin");
    const body = isStaff ? { staff_id: f.staff_id, pay_type: f.pay_type } : { category_id: f.category_id };
    try {
      await api.post(`/businesses/${bid}/entries`, { date, ...body, note: f.note, amount: parseFloat(String(f.amount).replace(",", ".")) });
      setF({ ...f, amount: "", note: "" });
      toast.success("Kayıt eklendi");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <form onSubmit={submit} className={`${card} space-y-4`} data-testid="entry-form">
      <h3 className="font-heading text-lg font-bold text-slate-900">Yeni Kayıt</h3>
      <div className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1">
        {[["income", "Girdi", "bg-emerald-600"], ["expense", "Çıktı", "bg-rose-600"], ["staff", "Personel", "bg-amber-600"]].map(([k, l, c]) => (
          <button type="button" key={k} data-testid={`entry-type-${k}`} onClick={() => { setType(k); setF({ ...f, category_id: "" }); }}
            className={`rounded-lg py-2 text-sm font-semibold transition-colors ${type === k ? `${c} text-white` : "text-slate-600"}`}>
            {l}
          </button>
        ))}
      </div>
      {isStaff ? (
        <div className="grid grid-cols-2 gap-2">
          <Select value={f.staff_id} onValueChange={(v) => setF({ ...f, staff_id: v })}>
            <SelectTrigger data-testid="entry-staff-select" className="h-11 rounded-xl"><SelectValue placeholder={staff.length ? "Personel seçin" : "Personel yok"} /></SelectTrigger>
            <SelectContent>
              {staff.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`entry-staff-option-${s.id}`}>{s.first_name} {s.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={f.pay_type} onValueChange={(v) => setF({ ...f, pay_type: v })}>
            <SelectTrigger data-testid="entry-pay-type-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="avans" data-testid="entry-pay-type-avans">Avans</SelectItem>
              <SelectItem value="maas" data-testid="entry-pay-type-maas">Maaş</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : (
      <Select value={f.category_id} onValueChange={(v) => setF({ ...f, category_id: v })}>
        <SelectTrigger data-testid="entry-category-select" className="h-11 rounded-xl"><SelectValue placeholder="Kategori seçin" /></SelectTrigger>
        <SelectContent>
          {cats.map((c) => (
            <SelectItem key={c.id} value={c.id} data-testid={`entry-category-option-${c.id}`}>{c.name}{c.is_opening ? " (Kasa Açılış)" : ""}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      )}
      <Input data-testid="entry-amount-input" inputMode="decimal" required placeholder="Tutar (₺)" value={f.amount}
        onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-11 rounded-xl font-mono" />
      <Input data-testid="entry-note-input" placeholder="Açıklama (opsiyonel)" value={f.note}
        onChange={(e) => setF({ ...f, note: e.target.value })} className="h-11 rounded-xl" />
      <button data-testid="add-entry-button" className={`${btnPrimary} w-full`}><Plus className="h-4 w-4" /> Kaydet</button>
    </form>
  );
}

function EntryList({ title, items, tone, onDelete, testId }) {
  const total = items.filter((e) => !e.is_opening).reduce((s, e) => s + e.amount, 0);
  return (
    <div className={card} data-testid={testId}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
        <span className={`font-mono text-sm font-bold ${tone}`}>{tl(total)}</span>
      </div>
      {items.length === 0 && <p className="py-6 text-center text-sm text-slate-400">Kayıt yok</p>}
      <ul className="divide-y divide-slate-100">
        {items.map((e) => (
          <li key={e.id} className="group flex items-center gap-3 py-2.5" data-testid={`entry-row-${e.id}`}>
            <CategoryIcon name={e.category_icon} type={e.type} opening={e.is_opening} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-slate-800">{e.category_name}{e.staff_name && <span className="text-amber-700"> · {e.staff_name}</span>}</div>
              {e.note && <div className="truncate text-xs text-slate-500">{e.note}</div>}
            </div>
            <span className={`font-mono text-sm font-semibold ${e.is_opening ? "text-sky-700" : tone}`}>{tl(e.amount)}</span>
            <button data-testid={`delete-entry-${e.id}`} onClick={() => onDelete(e.id)} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

const num = (v) => parseFloat(String(v).replace(",", "."));

function CashCount({ s, regs, counts, onSave }) {
  const [v, setV] = useState({});
  useEffect(() => {
    const ids = new Set(regs.map((r) => r.id));
    setV(Object.fromEntries(Object.entries(counts || {}).filter(([k]) => ids.has(k))));
  }, [counts, regs]);
  const filled = Object.values(v).filter((x) => x !== "" && x != null && !isNaN(num(x)));
  const total = filled.reduce((a, x) => a + num(x), 0);
  const bg = s.difference == null ? "border-slate-200 bg-white" : s.difference < 0 ? "border-rose-300 bg-rose-50" : s.difference > 0 ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-2xl border p-5 shadow-sm sm:p-6 ${bg}`} data-testid="cash-count-card">
      <div className="flex items-center gap-2"><Calculator className="h-5 w-5 text-cyan-700" /><h3 className="font-heading text-lg font-bold text-slate-900">Gün Sonu Kasa Sayımı</h3></div>
      <p className="mt-1 text-xs text-slate-500">Kasada saydığınız nakit tutarını girin. Beklenen: <b className="font-mono">{tl(s.expected_cash)}</b></p>
      <div className="mt-4 space-y-2">
        {regs.map((r, i) => (
          <div key={r.id} className="flex items-center gap-2">
            <span className="w-28 shrink-0 truncate text-sm font-medium text-slate-700">{r.name}</span>
            <Input data-testid={i === 0 ? "counted-cash-input" : `counted-cash-input-${i}`} inputMode="decimal" placeholder="Sayılan (₺)" value={v[r.id] ?? ""}
              onChange={(e) => setV({ ...v, [r.id]: e.target.value })} className="h-10 rounded-xl bg-white font-mono" />
          </div>
        ))}
        <div className="flex items-center justify-between border-t border-slate-200 pt-2 text-sm">
          <span className="font-semibold text-slate-700">Toplam Sayılan</span>
          <span className="font-mono font-bold" data-testid="counted-cash-total">{filled.length ? tl(total) : "—"}</span>
        </div>
        <button data-testid="save-counted-cash-button" className={`${btnPrimary} w-full`}
          onClick={() => onSave(Object.fromEntries(Object.entries(v).filter(([, x]) => x !== "" && x != null && !isNaN(num(x))).map(([k, x]) => [k, num(x)])))}>
          Sayımı Kaydet
        </button>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <DiffBadge diff={s.difference} />
      </div>
      <p className="mt-3 text-xs text-slate-500">Kasa açığı veya fazlası bir sonraki güne devretmez; aylık raporda toplam olarak gösterilir.</p>
    </div>
  );
}

export default function Ledger() {
  const { current } = useBusiness();
  const [date, setDate] = useState(todayStr());
  const [day, setDay] = useState(null);
  const [cats, setCats] = useState([]);
  const [regs, setRegs] = useState([]);
  const [staff, setStaff] = useState([]);

  const load = useCallback(async () => {
    if (!current) return;
    const [d, c, r, st] = await Promise.all([
      api.get(`/businesses/${current.id}/day`, { params: { date } }),
      api.get(`/businesses/${current.id}/categories`),
      api.get(`/businesses/${current.id}/registers`),
      api.get(`/businesses/${current.id}/staff`),
    ]);
    setDay(d.data);
    setCats(c.data);
    setRegs(r.data);
    setStaff(st.data);
  }, [current, date]);

  useEffect(() => {
    load();
  }, [load]);

  const del = async (id) => {
    await api.delete(`/businesses/${current.id}/entries/${id}`);
    toast.success("Kayıt silindi");
    load();
  };
  const saveCash = async (counts) => {
    const { data } = await api.put(`/businesses/${current.id}/day/${date}/close`, { counts });
    setDay(data);
    toast.success("Kasa sayımı kaydedildi");
  };

  if (!day) return <div className="h-64 animate-pulse rounded-2xl bg-slate-200/60" />;
  const s = day.summary;
  const incomes = day.entries.filter((e) => e.type === "income");
  const expenses = day.entries.filter((e) => e.type === "expense");

  return (
    <div className="space-y-6" data-testid="ledger-page">
      <PageHeader title="Günlük Defter" subtitle={fmtDate(date)}>
        <button data-testid="prev-day-button" onClick={() => setDate(shiftDate(date, -1))} className={btnGhost}><ChevronLeft className="h-4 w-4" /></button>
        <Input data-testid="ledger-date-input" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-10 w-40 rounded-xl" />
        <button data-testid="next-day-button" onClick={() => setDate(shiftDate(date, 1))} className={btnGhost}><ChevronRight className="h-4 w-4" /></button>
        <button data-testid="today-button" onClick={() => setDate(todayStr())} className={btnGhost}>Bugün</button>
      </PageHeader>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="ledger-opening" label="Kasa Açılış" value={tl(s.opening)} tone="blue" />
        <StatCard testId="ledger-income" label="Girdiler" value={tl(s.income)} tone="green" />
        <StatCard testId="ledger-expense" label="Çıktılar" value={tl(s.expense)} tone="red" />
        <StatCard testId="ledger-expected" label="Beklenen Kasa" value={tl(s.expected_cash)} tone="navy" />
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6">
          <EntryForm bid={current.id} date={date} categories={cats} staff={staff} onDone={load} />
          <CashCount s={s} regs={regs} counts={day.counts} onSave={saveCash} />
        </div>
        <EntryList testId="income-list" title="Girdiler" items={incomes} tone="text-emerald-700" onDelete={del} />
        <EntryList testId="expense-list" title="Çıktılar" items={expenses} tone="text-rose-700" onDelete={del} />
      </div>
    </div>
  );
}
